import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTheme } from '../App';
import { useTheme as useThemeCtx } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Search, Sun, Moon, Menu, X, ChevronDown, User, Settings, LogOut, Shield, Globe, Bell, TrendingUp, Zap, Accessibility, Cloud, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, CloudFog, Snowflake, CloudSun, Timer, Bug, Heart, Wrench, Edit3 } from 'lucide-react';
import axios from 'axios';
import AuthModal from './AuthModal';
import NotificationsDropdown from './NotificationsDropdown';

const API = process.env.REACT_APP_BACKEND_URL;

const PORTAL_COLORS = {
  sky: 'text-sky-accent hover:text-sky-accent-dark',
  amber: 'text-amber-600 hover:text-amber-700',
  terracotta: 'text-terracotta hover:text-terracotta-dark',
  pink: 'text-pink-500 hover:text-pink-600',
  emerald: 'text-emerald-600 hover:text-emerald-700',
  blue: 'text-blue-600 hover:text-blue-700',
  brown: 'text-amber-800 hover:text-amber-900',
};

const MARKET_TICKERS = [
  { symbol: 'CROBEX', value: '2,148.32', change: '+1.2%', up: true },
  { symbol: 'EUR/HRK', value: '7.53', change: '+0.1%', up: true },
  { symbol: 'S&P 500', value: '5,892.14', change: '-0.3%', up: false },
  { symbol: 'BTC', value: '98,452', change: '+2.8%', up: true },
  { symbol: 'Gold', value: '2,684', change: '+0.5%', up: true },
];

const WEATHER_ICONS = {
  'sun': Sun,
  'cloud': Cloud,
  'cloud-sun': CloudSun,
  'cloud-rain': CloudRain,
  'cloud-drizzle': CloudDrizzle,
  'cloud-fog': CloudFog,
  'cloud-lightning': CloudLightning,
  'snowflake': Snowflake,
};

export default function Header() {
  const { theme, toggleTheme } = useTheme();
  const themeCtx = useThemeCtx();
  const { lang, switchLanguage, t, SUPPORTED_LANGS, LANG_NAMES, LANG_FLAGS } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const articleSlugMatch = location.pathname.match(/^\/article\/(.+)$/);
  const currentArticleSlug = articleSlugMatch ? decodeURIComponent(articleSlugMatch[1]) : '';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [localUser, setLocalUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [weather, setWeather] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [ostaloOpen, setOstaloOpen] = useState(false);
  const ostaloRef = useRef(null);
  const forecastRef = useRef(null);
  const [weatherCoords, setWeatherCoords] = useState({ lat: null, lon: null });
  const [menuConfig, setMenuConfig] = useState({ main_nav: [], ostalo: [], portals: [] });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ostaloRef.current && !ostaloRef.current.contains(e.target)) setOstaloOpen(false);
      if (forecastRef.current && !forecastRef.current.contains(e.target)) setShowForecast(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchMenuConfig = () => {
      const headers = {};
      const storedUser = localStorage.getItem('ct_user');
      if (storedUser) {
        try {
          const u = JSON.parse(storedUser);
          if (u?.role) headers['X-User-Role'] = u.role;
        } catch {}
      }
      axios.get(`${API}/api/menu/config`, { headers }).then(r => setMenuConfig(r.data)).catch(() => {
        setMenuConfig({
          main_nav: [
            {key:'vijesti',slug:'vijesti',label:'Vijesti'},
            {key:'politika',slug:'politika',label:'Politika'},
            {key:'sport',slug:'sport',label:'Sport'},
          ],
          ostalo: [
            {key:'gospodarstvo',slug:'gospodarstvo',label:'Gospodarstvo'},
            {key:'tehnologija',slug:'tehnologija',label:'Tehnologija'},
            {key:'kultura',slug:'kultura',label:'Kultura'},
            {key:'zdravlje',slug:'zdravlje',label:'Zdravlje'},
            {key:'putovanja',slug:'putovanja',label:'Putovanja'},
          ],
          portals: [
            {key:'vibeposao',label:'Times Posao',route:'/vibeposao',color:'sky'},
            {key:'oglasnik',label:'Times Oglasnik',route:'/classifieds',color:'amber'},
            {key:'recepti',label:'Times Recepti',route:'/recepti',color:'terracotta'},
            {key:'veze',label:'Times Veze',route:'/veze',color:'pink'},
            {key:'majstor',label:'Times Majstor',route:'/majstor',color:'emerald'},
            {key:'doktor',label:'Times Doktor',route:'/doktor',color:'blue'},
            {key:'najam',label:'Times Najam',route:'/najam',color:'brown'},
          ],
        });
      });
    };
    fetchMenuConfig();
  }, []);

  useEffect(() => {
    const fetchWeather = (lat, lon) => {
      const params = lat != null && lon != null ? `?lat=${lat}&lon=${lon}` : '';
      axios.get(`${API}/api/weather${params}`)
        .then(r => {
          setWeather(r.data);
          if (r.data.lat && r.data.lon) setWeatherCoords({ lat: r.data.lat, lon: r.data.lon });
        })
        .catch(() => {});
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => fetchWeather(),
        { timeout: 4000, maximumAge: 300000 }
      );
    } else {
      fetchWeather();
    }
    const interval = setInterval(() => fetchWeather(), 600000);
    return () => clearInterval(interval);
  }, []);

  const handleWeatherClick = () => {
    if (showForecast) {
      setShowForecast(false);
      return;
    }
    const params = weatherCoords.lat ? `?lat=${weatherCoords.lat}&lon=${weatherCoords.lon}` : '';
    axios.get(`${API}/api/weather/forecast${params}`)
      .then(r => { setForecast(r.data); setShowForecast(true); })
      .catch(() => {});
  };

  useEffect(() => {
    const checkLocalAuth = () => {
      const stored = localStorage.getItem('ct_user');
      if (stored) { try { setLocalUser(JSON.parse(stored)); } catch {} }
      else { setLocalUser(null); }
    };
    checkLocalAuth();
    window.addEventListener('ct_auth_change', checkLocalAuth);
    return () => window.removeEventListener('ct_auth_change', checkLocalAuth);
  }, []);

  useEffect(() => {
    const currentUser = user || localUser;
    if (currentUser?.sub || currentUser?.email) {
      axios.get(`${API}/api/users/me`, {
        headers: {
          'X-User-Sub': currentUser.sub || '',
          'X-User-Email': currentUser.email || '',
          'X-User-Name': currentUser.name || ''
        }
      }).then(r => setUserRole(r.data?.role || 'user')).catch(() => {});
    }
  }, [isAuthenticated, user, localUser]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem('ct_token');
    localStorage.removeItem('ct_user');
    setLocalUser(null);
    window.dispatchEvent(new Event('ct_auth_change'));
    setUserMenuOpen(false);
    if (isAuthenticated) logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isAdmin = userRole === 'admin' || userRole === 'publisher';
  const currentUser = user || localUser;
  const loggedIn = isAuthenticated || !!localUser;

  const dateStr = new Date().toLocaleDateString(
    lang === 'hr' ? 'hr-HR' : lang === 'de' ? 'de-DE' : lang === 'fr' ? 'fr-FR' : 'en-GB',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  );

  const ForecastIcon = ({ iconKey, size = 16 }) => {
    const Icon = WEATHER_ICONS[iconKey] || Cloud;
    return <Icon size={size} />;
  };

  return (
    <>
      {/* Top Utility Bar */}
      <div className="bg-navy dark:bg-navy-dark text-white/80 text-xs border-b border-white/5 hidden md:block" data-testid="utility-bar">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-8">
          <div className="flex items-center gap-4">
            <span className="font-display font-medium capitalize">{dateStr}</span>
            <span className="text-white/20">|</span>
            {/* Weather Widget - clickable for 7-day forecast */}
            {weather && (
              <div className="relative" ref={forecastRef}>
                <button
                  onClick={handleWeatherClick}
                  className="flex items-center gap-1.5 hover:text-sky-accent transition-colors cursor-pointer"
                  data-testid="weather-widget"
                >
                  {(() => {
                    const WeatherIcon = WEATHER_ICONS[weather.icon] || Cloud;
                    return <WeatherIcon size={12} className="text-sky-accent" />;
                  })()}
                  <span className="font-medium text-white/90">{weather.temperature}°{weather.unit}</span>
                  <span className="text-white/50">{weather.city}</span>
                  <ChevronDown size={10} className={`transition-transform ${showForecast ? 'rotate-180' : ''}`} />
                </button>
                {showForecast && forecast && (
                  <div className="absolute left-0 top-full mt-2 bg-white dark:bg-navy-light shadow-2xl rounded-xl p-4 w-80 z-[200] border border-gray-100 dark:border-white/10 animate-fade-in" data-testid="weather-forecast-panel">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-display font-bold text-navy dark:text-white text-sm">{forecast.city} - 7 dana</h3>
                      <button onClick={() => setShowForecast(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                    </div>
                    {forecast.current && (
                      <div className="flex items-center gap-3 bg-sky-50 dark:bg-white/5 rounded-lg p-3 mb-3">
                        <ForecastIcon iconKey={forecast.current.icon} size={28} />
                        <div>
                          <div className="text-2xl font-bold text-navy dark:text-white">{forecast.current.temperature}°C</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{forecast.current.description_hr}</div>
                        </div>
                        <div className="ml-auto text-right text-[10px] text-gray-400">
                          <div>Vlaga: {forecast.current.humidity}%</div>
                          <div>Vjetar: {forecast.current.wind_speed} km/h</div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {forecast.forecast?.map((day, i) => (
                        <div key={day.date} className={`flex items-center gap-2 py-1.5 px-2 rounded-lg ${i === 0 ? 'bg-sky-accent/5' : 'hover:bg-gray-50 dark:hover:bg-white/5'} transition-colors`}>
                          <span className="w-8 text-[11px] font-display font-semibold text-navy dark:text-white">{day.day_name}</span>
                          <ForecastIcon iconKey={day.icon} size={16} />
                          <span className="flex-1 text-[11px] text-gray-500 dark:text-gray-400 truncate">{day.description_hr}</span>
                          <span className="text-[11px] font-medium text-navy dark:text-white">{day.temp_min}° / {day.temp_max}°</span>
                          {day.precipitation > 0 && <span className="text-[10px] text-blue-400">{day.precipitation}mm</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {weather && <span className="text-white/20">|</span>}
            <div className="flex items-center gap-1.5">
              <Zap size={11} className="text-sky-accent" />
              <span className="font-medium text-sky-accent-light">LIVE</span>
            </div>
            {/* Market Ticker */}
            <div className="flex items-center gap-3 ml-2">
              {MARKET_TICKERS.slice(0, 4).map(t => (
                <span key={t.symbol} className="flex items-center gap-1 whitespace-nowrap">
                  <span className="text-white/50 font-display">{t.symbol}</span>
                  <span className="font-medium text-white/90">{t.value}</span>
                  <span className={t.up ? 'text-emerald-400' : 'text-red-400'}>{t.change}</span>
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => window.dispatchEvent(new Event('toggle-accessibility'))} className="hover:text-sky-accent transition-colors flex items-center gap-1">
              <Accessibility size={11} />
              <span>{lang === 'hr' ? 'Pristupačnost' : 'Accessibility'}</span>
            </button>
            <span className="text-white/20">|</span>
            <button onClick={() => window.dispatchEvent(new Event('toggle-bug-report'))} className="hover:text-sky-accent transition-colors flex items-center gap-1" data-testid="bug-report-header-btn" title={lang === 'hr' ? 'Prijavi grešku' : 'Report a bug'}>
              <Bug size={11} />
            </button>
            <span className="text-white/20">|</span>
            {/* Language Switcher Mini */}
            <div className="relative">
              <button
                data-testid="utility-lang-switcher"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 hover:text-sky-accent transition-colors"
              >
                <Globe size={11} />
                <span className="uppercase font-display font-medium">{lang}</span>
                <ChevronDown size={10} />
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-2 bg-white dark:bg-navy-light shadow-xl rounded-xl py-1 w-44 z-[100] border border-gray-100 dark:border-white/10 animate-fade-in max-h-72 overflow-y-auto">
                  {SUPPORTED_LANGS.map(code => (
                    <button
                      key={code}
                      onClick={() => { switchLanguage(code); setLangMenuOpen(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-cream dark:hover:bg-white/5 transition-colors ${lang === code ? 'font-semibold text-sky-accent' : 'text-gray-700 dark:text-gray-300'}`}
                    >
                      <span>{LANG_FLAGS[code]}</span>
                      <span>{LANG_NAMES[code]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="text-white/20">|</span>
            <Link
              to="/subscription"
              data-testid="subscribe-cta-bar"
              className="px-3 py-1 bg-sky-accent hover:bg-sky-accent-dark text-white font-display font-semibold rounded-full transition-colors text-[11px] tracking-wide"
            >
              {t('subscribe')} €4.99/{t('per_month') || 'mo'}
            </Link>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'glass shadow-lg shadow-navy/5 dark:shadow-black/20 border-b border-gray-200/60 dark:border-white/5'
            : 'bg-white dark:bg-navy border-b border-gray-100 dark:border-white/5'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 lg:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0 group" data-testid="logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_d378754e-4674-4e88-b637-20552b388a6b/artifacts/82cxbgvt_CroatiaTimesLogoWebCMS.png"
                alt="Croatia Times"
                className="h-9 w-auto flex-shrink-0"
              />
              <div className="sr-only" aria-hidden="false">
                <h1>Croatia Times</h1>
                <p>{t('site_subtitle') || 'Croatia Today'}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-0.5" data-testid="main-nav">
              {menuConfig.main_nav.map(cat => (
                <Link
                  key={cat.key}
                  to={cat.route || `/category/${cat.slug}`}
                  className="px-2.5 py-1.5 text-[13px] font-display font-medium text-slate-600 dark:text-slate-300 hover:text-sky-accent dark:hover:text-sky-accent transition-colors tracking-wide whitespace-nowrap"
                >
                  {cat.label}
                </Link>
              ))}

              {/* Ostalo dropdown */}
              <div className="relative" ref={ostaloRef}>
                <button
                  onClick={() => setOstaloOpen(!ostaloOpen)}
                  className="px-2.5 py-1.5 text-[13px] font-display font-medium text-slate-600 dark:text-slate-300 hover:text-sky-accent dark:hover:text-sky-accent transition-colors tracking-wide flex items-center gap-0.5"
                  data-testid="ostalo-menu-btn"
                >
                  Ostalo <ChevronDown size={11} className={`transition-transform ${ostaloOpen ? 'rotate-180' : ''}`} />
                </button>
                {ostaloOpen && (
                  <div className="absolute left-0 top-full mt-1 bg-white dark:bg-navy-light shadow-xl rounded-xl py-2 w-48 z-[100] border border-gray-100 dark:border-white/10 animate-fade-in max-h-80 overflow-y-auto" data-testid="ostalo-dropdown">
                    {menuConfig.ostalo.map(cat => (
                      <Link
                        key={cat.key}
                        to={cat.route || `/category/${cat.slug}`}
                        className="block px-4 py-2 text-sm font-display text-slate-700 dark:text-slate-300 hover:bg-sky-accent/5 hover:text-sky-accent transition-colors"
                        onClick={() => setOstaloOpen(false)}
                      >
                        {cat.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {menuConfig.portals.map(portal => (
                <Link
                  key={portal.key}
                  to={portal.route}
                  className={`px-2.5 py-1 text-[13px] font-display font-semibold ${PORTAL_COLORS[portal.color] || 'text-sky-accent'} transition-colors tracking-wide text-center leading-tight`}
                  data-testid={`${portal.key}-nav-link`}
                >
                  <span className="block">{portal.label.split(' ')[0]}</span><span className="block">{portal.label.split(' ').slice(1).join(' ')}</span>
                </Link>
              ))}
              <Link
                to="/for-you"
                className="px-2.5 py-1 text-[13px] font-display font-medium text-terracotta hover:text-terracotta-dark transition-colors tracking-wide text-center leading-tight"
                data-testid="for-you-nav-link"
              >
                <span className="block">{lang === 'hr' ? 'Za' : 'For'}</span><span className="block">{lang === 'hr' ? 'vas' : 'You'}</span>
              </Link>
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-1.5">
              {/* Search */}
              {searchOpen ? (
                <form onSubmit={handleSearch} className="flex items-center gap-1.5 animate-fade-in">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={t('search')}
                    autoFocus
                    data-testid="search-input"
                    className="w-40 lg:w-56 px-3 py-1.5 border border-gray-200 dark:border-white/10 rounded-lg text-xs focus:outline-none focus:border-sky-accent focus:ring-1 focus:ring-sky-accent/30 dark:bg-navy-light dark:text-white font-display"
                  />
                  <button type="button" onClick={() => setSearchOpen(false)} className="p-1.5 text-gray-400 hover:text-gray-600">
                    <X size={14} />
                  </button>
                </form>
              ) : (
                <button
                  data-testid="search-button"
                  onClick={() => setSearchOpen(true)}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-sky-accent hover:bg-sky-accent/5 transition-all"
                >
                  <Search size={17} />
                </button>
              )}

              {/* Theme toggle */}
              <div className="relative group hidden sm:flex">
                <button
                  data-testid="theme-toggle"
                  onClick={() => { toggleTheme(); if (themeCtx?.toggle) themeCtx.toggle(); }}
                  className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-sky-accent hover:bg-sky-accent/5 transition-all"
                >
                  {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
                </button>
                {themeCtx?.autoSchedule && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-sky-accent rounded-full" title="Auto-schedule active" />
                )}
                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-navy-light shadow-xl rounded-lg py-1 w-44 z-[100] border border-gray-100 dark:border-white/10 hidden group-hover:block">
                  <button
                    data-testid="auto-dark-mode-btn"
                    onClick={() => { if (themeCtx?.toggleAutoSchedule) themeCtx.toggleAutoSchedule(); }}
                    className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-cream dark:hover:bg-white/5 flex items-center gap-2"
                  >
                    <Timer size={12} />
                    {themeCtx?.autoSchedule ? 'Isključi automatski' : 'Automatski (20h-7h)'}
                  </button>
                </div>
              </div>

              {/* Admin: Edit current article (only on /article/:slug pages) */}
              {isAdmin && currentArticleSlug && (
                <Link
                  to={`/admin/articles/edit-by-slug/${encodeURIComponent(currentArticleSlug)}`}
                  data-testid="admin-edit-article-btn"
                  title="Otvori članak za uređivanje"
                  className="hidden sm:flex p-2 rounded-lg text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-all"
                >
                  <Edit3 size={17} />
                </Link>
              )}

              {/* Notifications */}
              {loggedIn && <NotificationsDropdown />}

              {/* Subscribe button (desktop) */}
              <Link
                to="/subscription"
                data-testid="subscribe-btn"
                className="hidden lg:flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-sky-accent to-indigo-accent text-white rounded-full text-xs font-display font-semibold hover:shadow-lg hover:shadow-sky-accent/25 transition-all"
              >
                {t('subscribe')}
              </Link>

              {/* Auth */}
              {loggedIn ? (
                <div className="relative">
                  <button
                    data-testid="user-menu-button"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-navy dark:bg-white/10 text-white text-xs font-display font-medium hover:bg-navy-light transition-colors"
                  >
                    {currentUser?.picture ? (
                      <img src={currentUser.picture} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <User size={13} />
                    )}
                    <span className="hidden sm:block max-w-[80px] truncate">{(currentUser?.name || currentUser?.email)?.split(' ')[0]}</span>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-navy-light shadow-xl rounded-xl py-2 w-48 z-[100] border border-gray-100 dark:border-white/10 animate-fade-in">
                      <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-cream dark:hover:bg-white/5" onClick={() => setUserMenuOpen(false)}>
                        <User size={14} /> {t('myProfile')}
                      </Link>
                      {isAdmin && (
                        <Link to="/admin" className="flex items-center gap-2 px-4 py-2 text-sm text-sky-accent hover:bg-cream dark:hover:bg-white/5 font-medium" onClick={() => setUserMenuOpen(false)}>
                          <Shield size={14} /> {t('adminPanel')}
                        </Link>
                      )}
                      <hr className="my-1 border-gray-100 dark:border-white/10" />
                      <button onClick={handleSignOut} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10">
                        <LogOut size={14} /> {t('signOut')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  data-testid="login-button"
                  onClick={() => setShowAuthModal(true)}
                  className="px-3.5 py-1.5 bg-navy dark:bg-white/10 text-white rounded-full text-xs font-display font-semibold hover:bg-navy-light transition-colors"
                >
                  {t('signIn')}
                </button>
              )}

              {/* Mobile menu toggle */}
              <button
                className="lg:hidden p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-gray-100 dark:border-white/5 bg-white dark:bg-navy animate-slide-in fixed inset-x-0 top-14 bottom-0 z-40 overflow-y-auto" data-testid="mobile-nav">
            <div className="max-w-screen-2xl mx-auto px-4 py-3 pb-24">
              {/* Times Subportals Tiles */}
              <div className="grid grid-cols-3 gap-2 mb-4" data-testid="mobile-portal-tiles">
                {menuConfig.portals.map(portal => (
                  <Link
                    key={portal.key}
                    to={portal.route}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 dark:from-white/5 dark:to-white/10 border border-gray-100 dark:border-white/10 hover:shadow-md transition-all ${PORTAL_COLORS[portal.color] || 'text-sky-accent'}`}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-tile-${portal.key}`}
                  >
                    <span className="text-xs font-display font-bold leading-tight text-center">{portal.label}</span>
                  </Link>
                ))}
                <Link
                  to="/for-you"
                  className="flex flex-col items-center justify-center p-3 rounded-xl bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 border border-orange-100 dark:border-orange-500/20 hover:shadow-md transition-all text-terracotta"
                  onClick={() => setMobileOpen(false)}
                  data-testid="mobile-tile-for-you"
                >
                  <span className="text-xs font-display font-bold leading-tight text-center">{lang === 'hr' ? 'Za Vas' : 'For You'}</span>
                </Link>
              </div>

              <div className="border-t border-gray-100 dark:border-white/10 pt-3 mb-2">
                <p className="px-3 py-1 text-[10px] text-gray-400 font-display uppercase tracking-wider">{lang === 'hr' ? 'Kategorije' : 'Categories'}</p>
              </div>
              {menuConfig.main_nav.map(cat => (
                <Link
                  key={cat.key}
                  to={cat.route || `/category/${cat.slug}`}
                  className="block px-3 py-2.5 text-sm font-display font-medium text-slate-700 dark:text-slate-300 hover:text-sky-accent rounded-lg hover:bg-sky-accent/5"
                  onClick={() => setMobileOpen(false)}
                >
                  {cat.label}
                </Link>
              ))}
              <div className="border-t border-gray-100 dark:border-white/10 mt-2 pt-2">
                <p className="px-3 py-1 text-[10px] text-gray-400 font-display uppercase tracking-wider">Ostalo</p>
                {menuConfig.ostalo.map(cat => (
                  <Link key={cat.key} to={cat.route || `/category/${cat.slug}`} className="block px-3 py-2 text-sm font-display text-slate-600 dark:text-slate-400 hover:text-sky-accent" onClick={() => setMobileOpen(false)}>{cat.label}</Link>
                ))}
              </div>
              {/* Mobile language row */}
              <div className="flex flex-wrap gap-1.5 px-3 py-3 border-t border-gray-100 dark:border-white/10 mt-2">
                {SUPPORTED_LANGS.map(code => (
                  <button
                    key={code}
                    onClick={() => { switchLanguage(code); setMobileOpen(false); }}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-display font-medium transition-colors ${lang === code ? 'bg-sky-accent text-white' : 'bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400'}`}
                  >
                    {LANG_FLAGS[code]} {code.toUpperCase()}
                  </button>
                ))}
              </div>
              {/* Mobile theme toggle */}
              <div className="px-3 pb-2">
                <button
                  onClick={() => { toggleTheme(); if (themeCtx?.toggle) themeCtx.toggle(); }}
                  className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400"
                >
                  {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                  {theme === 'dark' ? t('light_mode') : t('dark_mode')}
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </>
  );
}
